import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutMultiGen } from '../layoutMulti';
import { OptsMulti } from '../optsMulti';
import { genMulti } from '../../core/genHelper';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/barcode';

import { IItemCount } from '@educraft/base';

export class TestSheet implements ILayoutMultiGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  private static createSimpleRNG(seed: number) {
    let state = seed >>> 0;
    return {
      next: () => {
        state = (state * 1664525 + 1013904223) >>> 0;
        return state;
      },
      nextInt: (min: number, max: number) => {
        state = (state * 1664525 + 1013904223) >>> 0;
        return min + (state % (max - min));
      },
    };
  }

  public async generate(pdf: PdfGen, opts: OptsMulti): Promise<void> {
    const questionsPerPage = 10;
    const optionsCount = 3;
    const totalQuestions = this.countDefault();

    const pageWidth = PdfSize.A4.width;
    const pageHeight = PdfSize.A4.height;

    const markerSize = PdfUnit.FromMillimeters(8);
    const markerOffset = PdfUnit.FromMillimeters(10);

    const qrSize = PdfUnit.FromMillimeters(25);
    const qrOffset = PdfUnit.FromMillimeters(10);

    const titleFont = new PdfFont(PdfFont.FONT_NAME, 24);
    const questionFont = new PdfFont(PdfFont.FONT_NAME, 12);
    const answerLabelFont = new PdfFont(PdfFont.FONT_NAME, 10);

    const answerSize = PdfUnit.FromMillimeters(15);
    const answerSpacingH = PdfUnit.FromMillimeters(35);
    const answerSpacingV = PdfUnit.FromMillimeters(22);
    const answerOriginX = PdfUnit.FromMillimeters(20);
    const answerOriginY = PdfUnit.FromMillimeters(70);

    const bubblePen = new PdfPen(PdfColor.fromRgb(0, 0, 0), PdfUnit.FromMillimeters(0.5));
    const markerBrush = PdfBrush.Black;

    const seed = opts.data.random.next();
    const testId = `test-${opts.data.random.digitsInt32(6)}`;

    const rng = TestSheet.createSimpleRNG(seed);
    const gens = genMulti(opts.data, opts.generator, totalQuestions, false, false);

    for (const gen of gens) {
      if (gen.answers.length > optionsCount) {
        gen.answers = gen.answers.slice(0, optionsCount);
      }
      while (gen.answers.length < optionsCount) {
        gen.answers.push('(N/A)');
      }
      gen.answerIndex = rng.nextInt(0, optionsCount);
    }

    for (let i = 0; i < totalQuestions; i += questionsPerPage) {
      const page = pdf.addPage();
      const currentQuestions = Math.min(questionsPerPage, totalQuestions - i);

      const qrData = JSON.stringify({
        l: Math.floor(i / questionsPerPage) + 1,
        s: seed,
        n: currentQuestions,
        o: optionsCount,
        tid: testId,
      });

      const qrRect = new PdfRect(
        pageWidth - qrSize - qrOffset,
        markerOffset + markerSize + PdfUnit.FromMillimeters(2),
        qrSize,
        qrSize
      );
      page.drawQr(qrRect, PdfBrush.Black, qrData);

      const topLeftMarker = new PdfRect(markerOffset, markerOffset, markerSize, markerSize);
      page.drawRectangle(null, markerBrush, topLeftMarker);

      const topRightMarker = new PdfRect(
        pageWidth - markerOffset - markerSize,
        markerOffset,
        markerSize,
        markerSize
      );
      page.drawRectangle(null, markerBrush, topRightMarker);

      const bottomLeftMarker = new PdfRect(
        markerOffset,
        pageHeight - markerOffset - markerSize,
        markerSize,
        markerSize
      );
      page.drawRectangle(null, markerBrush, bottomLeftMarker);

      const bottomRightMarker = new PdfRect(
        pageWidth - markerOffset - markerSize,
        pageHeight - markerOffset - markerSize,
        markerSize,
        markerSize
      );
      page.drawRectangle(null, markerBrush, bottomRightMarker);

      const titleRect = new PdfRect(
        answerOriginX,
        qrOffset,
        qrRect.left - answerOriginX,
        markerSize
      );
      const titleText = opts.data.lang?.TestSheet_Title ?? 'Test';
      page.drawString(titleText, titleFont, PdfBrush.Black, null, titleRect, ETextAlign.Center);

      for (let q = 0; q < currentQuestions; q++) {
        const questionY = answerOriginY + q * answerSpacingV;

        const questionNumber = `${i + q + 1}.`;
        const numberRect = new PdfRect(
          answerOriginX - PdfUnit.FromMillimeters(10),
          questionY,
          PdfUnit.FromMillimeters(8),
          answerSize
        );
        page.drawString(
          questionNumber,
          questionFont,
          PdfBrush.Black,
          null,
          numberRect,
          ETextAlign.TopRight
        );

        for (let opt = 0; opt < optionsCount; opt++) {
          const optionX = answerOriginX + opt * answerSpacingH;

          const bubbleRect = new PdfRect(optionX, questionY, answerSize, answerSize);

          page.drawRectangle(bubblePen, null, bubbleRect);

          const label = String.fromCharCode(65 + opt);
          const labelX = optionX + answerSize / 2;
          const labelY = questionY - PdfUnit.FromMillimeters(4);
          page.drawString(
            label,
            answerLabelFont,
            PdfBrush.Black,
            null,
            new PdfRect(
              labelX - PdfUnit.FromMillimeters(3),
              labelY,
              PdfUnit.FromMillimeters(6),
              PdfUnit.FromMillimeters(4)
            ),
            ETextAlign.Center
          );
        }
      }
    }
  }
}
